import { Spinner } from '@/components/glitch-ui/spinner';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { mediaViewModeAtom } from '@/store';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, HelpCircle, ImageIcon, Plus, Search, Sparkles, Star, Trash2, X } from 'lucide-react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { FilterDropdown, type FilterDropdownState } from '@/components/glitch-ui/filter-dropdown';
import { AssetSizeGuideDialog } from '@/components/glitch-ui/asset-size-guide-dialog';
import { GenerateImageDialog } from '@/components/glitch-ui/generate-image-dialog';
import { UploadQueueModal, type UploadQueueItem } from '@/components/glitch-ui/upload-queue-modal';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/useToast';

import { Button } from '@/components/ui/button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import {
  creativeAssetsInfiniteQueryOptions,
  useAddCreativeAssetTagMutation,
  useDeleteCreativeAssetMutation,
  useRemoveCreativeAssetTagMutation,
  useSetCreativeAssetPrimaryLogoMutation,
  useUnsetCreativeAssetPrimaryLogoMutation,
  useUploadCreativeAssetMutation,
  useUploadCreativeAssetZipMutation,
} from '@/lib/query-options';
import type { AdPlatform, CreativeAsset, CreativeAssetSource } from '@/lib/types';


/**
 * Map an upload/delete failure to a user-friendly message (#4650).
 *
 * Network failures (offline, server unreachable, CORS) surface as a raw ``TypeError: "Failed to fetch"``
 * because the request never completes — that must never reach the user. Server (HTTP) errors already
 * carry a normalised ``detail`` via ``safeErrorDetail`` in ``apis.ts``, so those are relayed as-is.
 */
function friendlyAssetError(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    // A fetch network failure surfaces as a TypeError, but with a browser-specific message; match the
    // message, not the bare name, so a genuine TypeError (a code bug) isn't masked as "offline" (#6163).
    const isNetworkFailure =
      err.message === 'Failed to fetch' || // Chrome
      err.message === 'Load failed' || // Safari
      err.message.startsWith('NetworkError'); // Firefox
    if (isNetworkFailure) {
      return 'Please check your connection and try again.';
    }
    if (err.message.startsWith('Request timeout')) {
      return 'The request timed out. Please try again.';
    }
    return err.message;
  }
  return fallback;
}

// Client-side upload pre-checks (#4784). Mirror the backend policy so oversized or
// unsupported files fail their queue row instantly, before any network call.
const MAX_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024;
const MAX_ZIP_UPLOAD_BYTES = 100 * 1024 * 1024;
const ACCEPTED_IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

const isZipFile = (file: File) => file.name.toLowerCase().endsWith('.zip') || file.type === 'application/zip';

function preflightError(file: File): string | null {
  if (isZipFile(file)) {
    return file.size > MAX_ZIP_UPLOAD_BYTES ? 'Too large. ZIP archives can be up to 100MB.' : null;
  }
  if (!ACCEPTED_IMAGE_MIME_TYPES.has(file.type)) return 'Unsupported type. JPG, PNG, GIF, WEBP, or ZIP.';
  if (file.size > MAX_IMAGE_UPLOAD_BYTES) return 'Too large. Images can be up to 10MB.';
  return null;
}

interface ImageGalleryProps {
  title?: string;
  baseText?: string;
  /** Page size for the infinite query — NOT a total cap; scrolling loads further pages. */
  pageSize?: number;
  platform?: AdPlatform;
  source?: CreativeAssetSource;
  mode?: 'library' | 'picker';
  selectedAssetIds?: number[];
  onSelect?: (asset: CreativeAsset) => void;
  /** When provided, the toolbar (Import, search, filter) is handed to the parent and the internal header row is hidden. */
  onControlsReady?: (controls: React.ReactNode) => void;
}

// Google Display Network accepted sizes (exact pixel dimensions).
const GOOGLE_DISPLAY_SIZES = new Set([
  '300x250', '336x280', '728x90', '300x600', '160x600',
  '320x50', '320x100', '468x60', '250x250', '200x200',
  '970x250', '970x90',
]);

function getPlatformFits(width: number, height: number): string[] {
  const fits: string[] = [];
  const ratio = width / height;

  // Meta Feed shows only for the recommended feed ratios (1:1, 4:5, 1.91:1). Meta technically
  // accepts the whole 4:5–1.91:1 range, but off-ratio images get cropped or letterboxed — the
  // chip marks a clean full-bleed fit, matching the exact-fit semantics of the other chips.
  if (width === height || width * 5 === height * 4 || Math.abs(ratio - 1.91) <= 0.02) fits.push('Meta Feed');

  // Meta Stories: exactly 9:16. Integer multiplication avoids floating-point error.
  if (width * 16 === height * 9) fits.push('Meta Stories');

  // Google Display: must match one of the platform's fixed accepted dimensions exactly.
  if (GOOGLE_DISPLAY_SIZES.has(`${width}x${height}`)) fits.push('Google Display');

  // Google Logo: exactly 1:1 (square, ≥128px) or exactly 4:1 (landscape, ≥512px wide).
  if (width === height && width >= 128) fits.push('Google Logo');
  if (width === height * 4 && width >= 512) fits.push('Google Logo');

  // Google YouTube: exactly 16:9 and at least 1280×720.
  if (width * 9 === height * 16 && width >= 1280 && height >= 720) fits.push('Google YouTube');

  return [...new Set(fits)];
}

// Ad-relevant aspect ratios per the paid-media size guide: 1:1, 4:5, 9:16, 16:9, 1.91:1, and 4:1
// (landscape logo). Exact integer math where platforms require exact ratios; 1.91:1 tolerates
// rounding because the canonical 1200×628 isn't exactly 1.91.
function getAdAspectRatio(width: number, height: number): string | null {
  if (width === height) return '1:1';
  if (width * 5 === height * 4) return '4:5';
  if (width * 16 === height * 9) return '9:16';
  if (width * 9 === height * 16) return '16:9';
  if (width === height * 4) return '4:1';
  if (Math.abs(width / height - 1.91) <= 0.02) return '1.91:1';
  return null;
}

// Primary-logo designation (#4776). The tag values are reserved server-side and only writable
// via the dedicated primary-logo endpoint; the qualification check mirrors the backend policy
// (Google logo specs: square 1:1 ≥128×128 or landscape 4:1 ≥512×128, PNG/JPEG, ≤5120KB) so the
// action only appears on assets the server would accept.
const PRIMARY_LOGO_TAG_VALUES = new Set(['primary-logo', 'primary-logo-landscape']);

function qualifiesAsPrimaryLogo(asset: CreativeAsset): boolean {
  if (!asset.width_px || !asset.height_px) return false;
  if (asset.mime_type !== 'image/png' && asset.mime_type !== 'image/jpeg') return false;
  if (asset.byte_size > 5120 * 1024) return false;
  const isSquare = asset.width_px === asset.height_px && asset.width_px >= 128;
  const isLandscape = asset.width_px === asset.height_px * 4 && asset.width_px >= 512;
  return isSquare || isLandscape;
}

const toTitleCase = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase());


const PLATFORM_FIT_COLORS: Record<string, string> = {
  'Meta Feed': 'bg-blue-100 text-blue-800',
  'Meta Stories': 'bg-blue-100 text-blue-800',
  'Google Display': 'bg-green-100 text-green-800',
  'Google Logo': 'bg-green-100 text-green-800',
  'Google YouTube': 'bg-red-100 text-red-800',
};

// --- Chip primitives shared by the grid card and the table view (#6182) ---------------------------
// The two views rendered byte-copies of every chip/pill (#6163 P3); each piece now lives once here.

const CHIP_CLASS = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
const ADD_CHIP_CLASS = `${CHIP_CLASS} border border-dashed border-gray-300 text-gray-400 hover:border-[#290BD9] hover:text-[#290BD9] transition-colors`;

function RatioChip({ ratio }: { ratio: string }) {
  return <span className={`${CHIP_CLASS} bg-gray-100 text-gray-600`}>{ratio}</span>;
}

function PlatformFitChip({ fit }: { fit: string }) {
  return <span className={`${CHIP_CLASS} ${PLATFORM_FIT_COLORS[fit] ?? 'bg-gray-100 text-gray-600'}`}>{fit}</span>;
}

function LogoChip({ showUnset, disabled, onUnset }: { showUnset: boolean; disabled: boolean; onUnset: () => void }) {
  return (
    <span className={`${CHIP_CLASS} gap-1 bg-[#EDE9FE] text-[#290BD9] border border-transparent`}>
      <Star className='h-2.5 w-2.5' />
      Logo
      {showUnset && (
        <button
          disabled={disabled}
          className='ml-0.5 hover:text-red-600 leading-none disabled:opacity-50'
          onClick={(e) => { e.stopPropagation(); onUnset(); }}
          title='Unset logo'
        >
          <X className='h-2.5 w-2.5' />
        </button>
      )}
    </span>
  );
}

function SetLogoButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <button
      disabled={disabled}
      className={`${ADD_CHIP_CLASS} disabled:opacity-50`}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title='Set as logo'
    >
      + Logo
    </button>
  );
}

function TagChip({ tag, canRemove, onRemove }: { tag: CreativeAsset['tags'][number]; canRemove: boolean; onRemove: () => void }) {
  if (tag.tag_type !== 'user') {
    return <span className={`${CHIP_CLASS} bg-amber-50 text-amber-600`}>{toTitleCase(tag.tag_value)}</span>;
  }
  return (
    <span className={`${CHIP_CLASS} gap-0.5 bg-purple-50 text-purple-600`}>
      {toTitleCase(tag.tag_value)}
      {canRemove && (
        <button
          className='ml-0.5 hover:text-red-600 leading-none'
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title={`Remove tag "${tag.tag_value}"`}
        >
          <X className='h-2.5 w-2.5' />
        </button>
      )}
    </span>
  );
}

function AddTagButton({ onClick }: { onClick: () => void }) {
  return (
    <button className={ADD_CHIP_CLASS} onClick={(e) => { e.stopPropagation(); onClick(); }} title='Add tag'>
      + Tag
    </button>
  );
}

/** The inline add-tag input. `disabled` doubles as the submitting flag (spinner + blur-cancel gate). */
function TagInput({ value, disabled, onChange, onSubmit, onCancel }: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <span className='relative inline-flex items-center'>
      <input
        autoFocus
        className='text-sm pl-1.5 pr-6 py-0.5 rounded border border-[#290BD9] w-24 outline-none bg-white text-gray-700 disabled:opacity-70'
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={() => { if (!value.trim() && !disabled) onCancel(); }}
        onClick={(e) => e.stopPropagation()}
        placeholder='tag name'
      />
      {disabled && <Spinner size="sm" className="absolute right-1.5 text-[#290BD9] pointer-events-none" />}
    </span>
  );
}

interface AssetCardProps {
  asset: CreativeAsset;
  mode: 'library' | 'picker';
  isSelected: boolean;
  isDeleting: boolean;
  isTogglingLogo: boolean;
  onRemove: (id: number) => void;
  onSelect?: (asset: CreativeAsset) => void;
  onAddUserTag: (asset: CreativeAsset, tagValue: string) => Promise<void>;
  onRemoveUserTag: (asset: CreativeAsset, tagValue: string) => Promise<void>;
  onSetPrimaryLogo: (asset: CreativeAsset) => void;
  onUnsetPrimaryLogo: (asset: CreativeAsset) => void;
}

const AssetCard = ({
  asset,
  mode,
  isSelected,
  isDeleting,
  isTogglingLogo,
  onRemove,
  onSelect,
  onAddUserTag,
  onRemoveUserTag,
  onSetPrimaryLogo,
  onUnsetPrimaryLogo,
}: AssetCardProps) => {
  const fits = asset.width_px && asset.height_px ? getPlatformFits(asset.width_px, asset.height_px) : [];
  const adRatio = asset.width_px && asset.height_px ? getAdAspectRatio(asset.width_px, asset.height_px) : null;
  const isPrimaryLogo = asset.tags.some((t) => PRIMARY_LOGO_TAG_VALUES.has(t.tag_value));
  const canBeLogo = qualifiesAsPrimaryLogo(asset);
  const filename = asset.original_filename ?? null;
  const clickable = mode === 'picker' && !!onSelect;

  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState('');
  const [isSubmittingTag, setIsSubmittingTag] = useState(false);

  const handleSubmitTag = async () => {
    const value = newTagValue.trim().toLowerCase();
    if (!value) {
      setIsAddingTag(false);
      setNewTagValue('');
      return;
    }
    setIsSubmittingTag(true);
    try {
      await onAddUserTag(asset, value);
    } finally {
      setIsSubmittingTag(false);
      setIsAddingTag(false);
      setNewTagValue('');
    }
  };

  return (
    <div
      className={
        'flex flex-col rounded-lg border bg-white overflow-hidden ' +
        (isSelected ? 'ring-2 ring-[#290BD9]' : 'border-gray-200') +
        (clickable ? ' cursor-pointer hover:shadow-md transition-shadow' : '')
      }
      onClick={clickable ? () => onSelect?.(asset) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect?.(asset);
              }
            }
          : undefined
      }
    >
      {/* Letterboxed thumbnail — shows true aspect ratio */}
      <div className='relative h-36 bg-slate-100 flex items-center justify-center'>
        <img
          src={asset.signed_url}
          alt={filename ?? `Asset ${asset.id}`}
          className='max-h-full max-w-full object-contain'
          loading='lazy'
        />
        {isSelected && (
          <div className='absolute inset-0 bg-[#290BD9]/10 flex items-center justify-center'>
            <div className='w-6 h-6 rounded-full bg-[#290BD9] flex items-center justify-center'>
              <Check className='h-3.5 w-3.5 text-white' aria-hidden='true' />
            </div>
          </div>
        )}
        {mode === 'library' && (
          <Button
            variant='ghost'
            size='sm'
            disabled={isDeleting}
            className='absolute top-1 right-1 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 hover:bg-black/50 hover:text-white rounded-md'
            onClick={(e) => { e.stopPropagation(); onRemove(asset.id); }}
          >
            <Trash2 className='h-4 w-4' />
          </Button>
        )}
      </div>

      {/* Info strip */}
      <div className='p-2.5 flex flex-col flex-1 gap-1.5'>
        {filename && (
          <p className='text-base font-medium text-gray-900 truncate' title={filename}>
            {filename}
          </p>
        )}

        {/* Dimensions + ratio + logo designation */}
        <div className='flex items-center gap-1.5 flex-wrap'>
          {asset.width_px && asset.height_px && (
            <span className='text-sm text-gray-500'>
              {asset.width_px}×{asset.height_px}
            </span>
          )}
          {adRatio && <RatioChip ratio={adRatio} />}
          {isPrimaryLogo && (
            <LogoChip showUnset={mode === 'library'} disabled={isTogglingLogo} onUnset={() => onUnsetPrimaryLogo(asset)} />
          )}
          {!isPrimaryLogo && canBeLogo && mode === 'library' && (
            <SetLogoButton disabled={isTogglingLogo} onClick={() => onSetPrimaryLogo(asset)} />
          )}
        </div>

        {fits.length > 0 && <div className='border-t border-gray-100 my-1' />}

        {/* Platform fit chips */}
        {fits.length > 0 && (
          <div className='flex flex-wrap gap-1'>
            {fits.map((fit) => (
              <PlatformFitChip key={fit} fit={fit} />
            ))}
          </div>
        )}

        {fits.length > 0 && (asset.tags.filter((t) => !PRIMARY_LOGO_TAG_VALUES.has(t.tag_value)).length > 0 || mode === 'library') && (
          <div className='border-t border-gray-100 my-1' />
        )}

        {/* Tags: user/system tags + add input */}
        {(asset.tags.filter((t) => !PRIMARY_LOGO_TAG_VALUES.has(t.tag_value)).length > 0 || mode === 'library') && (
          <div className='flex flex-wrap gap-1'>
            {asset.tags.filter((tag) => !PRIMARY_LOGO_TAG_VALUES.has(tag.tag_value)).map((tag) => (
              <TagChip
                key={tag.id}
                tag={tag}
                canRemove={mode === 'library'}
                onRemove={() => onRemoveUserTag(asset, tag.tag_value)}
              />
            ))}

            {/* Inline tag input */}
            {mode === 'library' && (
              isAddingTag ? (
                <TagInput
                  value={newTagValue}
                  disabled={isSubmittingTag}
                  onChange={setNewTagValue}
                  onSubmit={handleSubmitTag}
                  onCancel={() => { setIsAddingTag(false); setNewTagValue(''); }}
                />
              ) : (
                <AddTagButton onClick={() => setIsAddingTag(true)} />
              )
            )}
          </div>
        )}

        {/* Library actions */}
        {mode === 'library' && (
          <div className='flex items-center pt-0.5 mt-auto'>
            <Button
              variant='ghost'
              size='sm'
              disabled={isDeleting}
              className='h-7 text-sm px-2 text-gray-400 hover:text-red-600 hover:bg-red-50 ml-auto'
              onClick={(e) => { e.stopPropagation(); onRemove(asset.id); }}
            >
              <Trash2 className='h-4 w-4 mr-1' />
              Remove
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const ImageGallery = ({
  title = 'Images',
  baseText = 'From the asset library',
  pageSize = 500,
  platform,
  source,
  mode = 'library',
  selectedAssetIds = [],
  onSelect,
  onControlsReady,
}: ImageGalleryProps) => {
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSelections, setFilterSelections] = useState<Record<string, string[]>>({});
  const [viewMode, setViewMode] = useAtom(mediaViewModeAtom);
  const dragCounterRef = useRef(0);

  // Debounce the search box so each keystroke doesn't fire a request (#5383).
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Search + tag filtering run server-side so results stay correct across the whole library, not
  // just the loaded pages (#5383). Platform-fit stays a client-side refinement of loaded rows.
  const tagFilter = filterSelections.tags ?? [];
  const listQuery = useInfiniteQuery(
    creativeAssetsInfiniteQueryOptions({
      limit: pageSize,
      platform,
      source,
      search: debouncedSearch || undefined,
      tags: tagFilter.length > 0 ? tagFilter : undefined,
    }),
  );
  const uploadOne = useUploadCreativeAssetMutation();
  const uploadZip = useUploadCreativeAssetZipMutation();
  const deleteAsset = useDeleteCreativeAssetMutation();
  const addTag = useAddCreativeAssetTagMutation();
  const removeTag = useRemoveCreativeAssetTagMutation();
  const setLogo = useSetCreativeAssetPrimaryLogoMutation();
  const unsetLogo = useUnsetCreativeAssetPrimaryLogoMutation();

  const items = useMemo(() => listQuery.data?.pages.flatMap((p) => p.items) ?? [], [listQuery.data]);
  const total = listQuery.data?.pages[0]?.total ?? 0;

  const allTagValues = [...new Set(items.flatMap((a) => a.tags.map((t) => t.tag_value)))].sort();

  const activeFilterCount = (filterSelections.platformFit?.length ?? 0) + (filterSelections.tags?.length ?? 0);

  // Search + tags are already applied server-side (#5383); only the computed platform-fit filter
  // runs client-side, over the loaded rows.
  const platformFilter = filterSelections.platformFit ?? [];
  const filteredItems =
    platformFilter.length === 0
      ? items
      : items.filter((asset) => {
          const fits = asset.width_px && asset.height_px ? getPlatformFits(asset.width_px, asset.height_px) : [];
          return platformFilter.some((f) => fits.includes(f));
        });

  // Infinite scroll (#5383): load the next page when the sentinel below the grid nears the viewport.
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = listQuery;
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, filteredItems.length]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const triggerUpload = () => fileInputRef.current?.click();

  // Upload queue (#4784) — one row per file, each with an independent outcome. The modal is
  // feedback only; this loop keeps running if the user closes it mid-batch.
  const [queueItems, setQueueItems] = useState<UploadQueueItem[]>([]);
  const [isQueueOpen, setIsQueueOpen] = useState(false);
  const queueFilesRef = useRef<Map<string, File>>(new Map());
  const queueIdRef = useRef(0);

  const updateQueueItem = (id: string, patch: Partial<UploadQueueItem>) =>
    setQueueItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  // One upload at a time (#6163 P1-10, #6179): every upload — batch item or retry — runs through
  // ``enqueueUpload``'s promise chain, so callers WAIT their turn instead of silently skipping when
  // another upload is in flight (the old boolean skip stranded a batch started during an in-flight
  // retry in 'waiting' forever). ``pendingUploadIdsRef`` dedupes per item (double-clicked Retry),
  // and ``batchDepthRef`` still blocks Retry clicks while any batch runs.
  const uploadChainRef = useRef<Promise<void>>(Promise.resolve());
  const pendingUploadIdsRef = useRef<Set<string>>(new Set());
  const batchDepthRef = useRef(0);

  const enqueueUpload = (id: string): Promise<void> => {
    if (pendingUploadIdsRef.current.has(id)) return Promise.resolve();
    pendingUploadIdsRef.current.add(id);
    const turn = uploadChainRef.current
      .then(() => runUpload(id))
      .finally(() => pendingUploadIdsRef.current.delete(id));
    // runUpload never throws (it catches into item status), but keep the chain unbreakable anyway.
    uploadChainRef.current = turn.catch(() => {});
    return turn;
  };

  const runUpload = async (id: string) => {
    const file = queueFilesRef.current.get(id);
    if (!file) return;
    const error = preflightError(file);
    if (error) {
      updateQueueItem(id, { status: 'failed', detail: error });
      return;
    }
    updateQueueItem(id, { status: 'uploading', detail: undefined, skippedEntries: undefined });
    try {
      if (isZipFile(file)) {
        const result = await uploadZip.mutateAsync(file);
        const imported = result.items.length;
        const skipped = result.skipped.length;
        const detail =
          imported === 0
            ? `No images imported${skipped > 0 ? ` · ${skipped} skipped` : ''}`
            : `${imported} image${imported === 1 ? '' : 's'} imported${skipped > 0 ? ` · ${skipped} skipped` : ''}`;
        updateQueueItem(id, {
          status: imported === 0 && skipped > 0 ? 'failed' : 'done',
          detail,
          skippedEntries: result.skipped.length > 0 ? result.skipped : undefined,
        });
      } else {
        await uploadOne.mutateAsync(file);
        updateQueueItem(id, { status: 'done' });
      }
    } catch (uploadError) {
      updateQueueItem(id, {
        status: 'failed',
        detail: friendlyAssetError(uploadError, 'Upload failed. Please try again.'),
      });
    }
  };

  const uploadFiles = async (files: File[]) => {
    const newItems: UploadQueueItem[] = files.map((file) => {
      const id = `upload-${queueIdRef.current++}`;
      queueFilesRef.current.set(id, file);
      const error = preflightError(file);
      return {
        id,
        filename: file.name,
        byteSize: file.size,
        isZip: isZipFile(file),
        status: error ? 'failed' : 'waiting',
        detail: error ?? undefined,
      };
    });
    setQueueItems((prev) => [...prev, ...newItems]);
    setIsQueueOpen(true);
    // Sequential on purpose (matches the previous behavior and avoids hammering GCS), but a
    // failure no longer aborts the batch — every remaining file still gets its turn. A counter
    // (not a boolean) so overlapping drops keep the Retry gate up until the LAST batch finishes.
    batchDepthRef.current += 1;
    try {
      for (const item of newItems) {
        if (item.status === 'failed') continue;
        await enqueueUpload(item.id);
      }
    } finally {
      batchDepthRef.current -= 1;
    }
  };

  // Retry ignores clicks while a batch is running, so batches keep strict priority; outside a
  // batch, retries queue through the chain and cannot double-upload (#6163 P1-10, #6179).
  const handleRetry = (id: string) => {
    if (batchDepthRef.current > 0) return;
    void enqueueUpload(id);
  };

  const handleQueueClose = () => {
    setIsQueueOpen(false);
    // Clear a finished queue so the next batch starts fresh; an active run keeps its rows.
    setQueueItems((prev) => {
      if (prev.some((i) => i.status === 'uploading' || i.status === 'waiting')) return prev;
      queueFilesRef.current.clear();
      return [];
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    await uploadFiles(files);
    event.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    await uploadFiles(files);
  };

  const handleAddUserTag = async (asset: CreativeAsset, tagValue: string) => {
    try {
      await addTag.mutateAsync({ assetId: asset.id, tagValue, tagType: 'user' });
    } catch (tagError) {
      toast({
        title: 'Tag add failed',
        description: friendlyAssetError(tagError, 'Could not add the tag.'),
        variant: 'destructive',
      });
    }
  };

  const handleRemoveUserTag = async (asset: CreativeAsset, tagValue: string) => {
    try {
      await removeTag.mutateAsync({ assetId: asset.id, tagValue });
    } catch (tagError) {
      toast({
        title: 'Tag remove failed',
        description: friendlyAssetError(tagError, 'Could not remove the tag.'),
        variant: 'destructive',
      });
    }
  };

  const handleSetPrimaryLogo = async (asset: CreativeAsset) => {
    try {
      await setLogo.mutateAsync(asset.id);
    } catch (logoError) {
      toast({
        title: 'Could not set primary logo',
        description: friendlyAssetError(logoError, 'Please try again.'),
        variant: 'destructive',
      });
    }
  };

  const handleUnsetPrimaryLogo = async (asset: CreativeAsset) => {
    try {
      await unsetLogo.mutateAsync(asset.id);
    } catch (logoError) {
      toast({
        title: 'Could not unset primary logo',
        description: friendlyAssetError(logoError, 'Please try again.'),
        variant: 'destructive',
      });
    }
  };

  const [deleteAssetId, setDeleteAssetId] = useState<number | null>(null);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [tableTagAssetId, setTableTagAssetId] = useState<number | null>(null);
  const [tableTagValue, setTableTagValue] = useState('');
  const [isSubmittingTableTag, setIsSubmittingTableTag] = useState(false);

  const handleSubmitTableTag = async () => {
    const value = tableTagValue.trim().toLowerCase();
    if (!value) {
      setTableTagAssetId(null);
      setTableTagValue('');
      return;
    }
    const asset = filteredItems.find((a) => a.id === tableTagAssetId);
    if (!asset) return;
    setIsSubmittingTableTag(true);
    try {
      await handleAddUserTag(asset, value);
    } finally {
      setIsSubmittingTableTag(false);
      setTableTagAssetId(null);
      setTableTagValue('');
    }
  };

  const requestRemove = (assetId: number) => setDeleteAssetId(assetId);

  const confirmRemove = async () => {
    if (!deleteAssetId) return;
    try {
      await deleteAsset.mutateAsync(deleteAssetId);
      setDeleteAssetId(null);
    } catch (deleteError) {
      toast({
        title: 'Could not remove image',
        description: friendlyAssetError(deleteError, 'Please try again.'),
        variant: 'destructive',
      });
    }
  };

  const isUploading = uploadOne.isPending || uploadZip.isPending;
  const isDeleting = deleteAsset.isPending;
  // A search/filter that returns nothing is NOT an empty library — keep the controls visible so the
  // user can clear it. "Empty" means the library itself has no assets with no active query (#5383).
  const hasActiveQuery = debouncedSearch.length > 0 || tagFilter.length > 0 || platformFilter.length > 0;
  const isEmpty = !listQuery.isLoading && !listQuery.isError && items.length === 0 && !hasActiveQuery;

  const controlsElement = (
    <div className='flex items-center gap-2 min-w-0'>
      <Button
        variant='outline'
        className='text-gray-900 h-9'
        onClick={triggerUpload}
        disabled={isUploading}
      >
        {isUploading ? (
          <>
            <Spinner size="sm" className="mr-2" /> Uploading
          </>
        ) : (
          <>
            <Plus className='mr-2 h-4 w-4' /> Import
          </>
        )}
      </Button>
      <Button variant='outline' className='text-gray-900 h-9' onClick={() => setIsGenerateOpen(true)}>
        <Sparkles className='mr-2 h-4 w-4' /> Generate
      </Button>
      <Button variant='outline' className='text-gray-900 h-9' onClick={() => setIsSizeGuideOpen(true)}>
        <HelpCircle className='mr-2 h-4 w-4' /> Help
      </Button>
      {/* The one flexible element in the toolbar: shrinks (down to min-w-28) before anything
          else so the buttons and the gallery title keep their full width. */}
      <div className='relative w-44 min-w-28 shrink'>
        <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          placeholder='Search…'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='pl-9 h-9 w-full'
        />
      </div>
      <FilterDropdown
        showDateRange={false}
        popoverWidth='w-[240px]'
        sections={[
          {
            id: 'platformFit',
            title: 'Platform',
            options: [
              { value: 'Meta Feed', label: 'Meta Feed' },
              { value: 'Meta Stories', label: 'Meta Stories' },
              { value: 'Google Display', label: 'Google Display' },
              { value: 'Google Logo', label: 'Google Logo' },
              { value: 'Google YouTube', label: 'Google YouTube' },
            ],
          },
          ...(allTagValues.length > 0
            ? [{ id: 'tags', title: 'Tags', options: allTagValues.map((v) => ({ value: v, label: v })) }]
            : []),
        ]}
        selectedValues={filterSelections}
        activeFilterCount={activeFilterCount}
        onApply={(state: FilterDropdownState) => setFilterSelections(state.selections)}
        topSlot={mode === 'library' ? (
          <div>
            <h3 className='text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3'>View</h3>
            <ToggleSwitch
              mode='2-state'
              value={viewMode === 'table' ? 'left' : 'right'}
              leftLabel='Table'
              rightLabel='Grid'
              onChange={(v) => setViewMode(v === 'left' ? 'table' : 'grid')}
            />
          </div>
        ) : undefined}
      />
    </div>
  );

  // Pass controls to parent if callback provided. The empty state carries its own Import CTA,
  // so no toolbar is handed up until the library has images.
  useEffect(() => {
    if (onControlsReady) {
      onControlsReady(isEmpty ? null : controlsElement);
    }
  }, [onControlsReady, searchQuery, filterSelections, isUploading, isEmpty, items, viewMode]);

  return (
    <div
      className='relative flex flex-col h-full'
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type='file'
        className='hidden'
        accept='image/png,image/jpeg,image/gif,image/webp,application/zip,.zip'
        multiple
        onChange={handleFileChange}
      />

      <UploadQueueModal open={isQueueOpen} onClose={handleQueueClose} items={queueItems} onRetry={handleRetry} />
      <GenerateImageDialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen} />
      <AssetSizeGuideDialog open={isSizeGuideOpen} onOpenChange={setIsSizeGuideOpen} />

      {/* Drop overlay */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            className='absolute inset-0 z-10 rounded-lg border-2 border-dashed border-[#290BD9] bg-white flex items-center justify-center pointer-events-none'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div className='text-center'>
              <ImageIcon className='h-8 w-8 text-[#290BD9] mx-auto mb-2' strokeWidth={1.5} />
              <p className='text-sm font-medium text-[#290BD9]'>Drop to upload</p>
              <p className='text-xs text-[#290BD9]/70 mt-0.5'>JPG, PNG, GIF, WEBP, or ZIP. Up to 10MB per image.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {isEmpty ? (
        <>
          <EmptyState
            icon={<ImageIcon className='h-12 w-12 text-gray-400' strokeWidth={1.5} />}
            title='No Images Yet'
            description={
              mode === 'picker'
                ? 'Upload or drag and drop images. PNG, JPG, GIF, WEBP, or a ZIP of images.'
                : 'Drag and drop images here, or click Import to get started.'
            }
            action={
              <div className='flex items-center gap-3'>
                <Button
                  size='lg'
                  className='py-6 bg-[#290BD9] hover:bg-[#2309C5] flex items-center justify-center space-x-2'
                  onClick={triggerUpload}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <Spinner size="sm" />
                      <span>UPLOADING…</span>
                    </>
                  ) : (
                    <>
                      <Plus className='h-4 w-4' />
                      <span>IMPORT</span>
                    </>
                  )}
                </Button>
                {/* Generation must be reachable from an EMPTY library too (#4332) — it's
                    how a library gets its first images without anything to upload. */}
                <Button
                  size='lg'
                  variant='outline'
                  className='py-6 text-gray-900 flex items-center justify-center space-x-2'
                  onClick={() => setIsGenerateOpen(true)}
                >
                  <Sparkles className='h-4 w-4' />
                  <span>GENERATE</span>
                </Button>
              </div>
            }
          />
        </>
      ) : (
        <>
          {!onControlsReady && (
            // Title left, toolbar right on one line. The subtext that used to force a second title
            // line (misaligning this row) is gone, so a single-line title sits cleanly with items-center;
            // the title block never shrinks — the search inside the toolbar gives way instead.
            <div className='mb-4 flex items-center justify-between gap-4'>
              <div className='shrink-0'>
                {(title || hasActiveQuery) && (
                  <div className='flex items-baseline gap-2'>
                    {title && <h2 className='text-xl font-semibold text-gray-900'>{title}</h2>}
                    {/* Count only earns its space once a search/filter is on ("3 of 20"); an idle
                        "1 of 1" is just noise. */}
                    {hasActiveQuery && (
                      <span className='text-sm text-gray-500'>{filteredItems.length} of {total}</span>
                    )}
                  </div>
                )}
                {baseText && <p className='text-sm text-gray-600 mt-1'>{baseText}</p>}
              </div>
              {controlsElement}
            </div>
          )}

          {listQuery.isLoading && (
            <div className='flex items-center justify-center py-8 text-sm text-gray-500'>
              <Spinner size="sm" className="mr-2" /> Loading library
            </div>
          )}

          {listQuery.isError && (
            <p className='py-4 text-sm text-red-700'>Could not load the asset library. Try again.</p>
          )}

          {!listQuery.isLoading && filteredItems.length === 0 && hasActiveQuery && (
            <EmptyState
              icon={<Search className='h-12 w-12 text-gray-400' strokeWidth={1.5} />}
              title='No Results'
              description='No assets match your search or filters. Try adjusting your search or clearing filters.'
            />
          )}

          {!listQuery.isLoading && filteredItems.length > 0 && viewMode === 'grid' && (
            // Picker lives in the narrow wizard card, so keep 3 across for roomier cards; the
            // full-width /assets library can go denser (4) on xl.
            <div
              className={`grid gap-4 ${
                mode === 'picker'
                  ? // Cap the grid and scroll it internally so the wizard's BACK/NEXT stay in view; the
                    // description + toolbar above stay pinned. Library mode flows with the page. The
                    // border + drop shadow frame the scroll area as a distinct box inside the card.
                    'grid-cols-3 max-h-[50vh] overflow-y-auto rounded-lg border border-gray-200 p-2 shadow-md'
                  : 'grid-cols-3 xl:grid-cols-4'
              }`}
            >
              {filteredItems.map((asset) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  mode={mode}
                  isSelected={selectedAssetIds.includes(asset.id)}
                  isDeleting={isDeleting}
                  isTogglingLogo={setLogo.isPending || unsetLogo.isPending}
                  onRemove={requestRemove}
                  onSelect={onSelect}
                  onAddUserTag={handleAddUserTag}
                  onRemoveUserTag={handleRemoveUserTag}
                  onSetPrimaryLogo={handleSetPrimaryLogo}
                  onUnsetPrimaryLogo={handleUnsetPrimaryLogo}
                />
              ))}
            </div>
          )}

          {!listQuery.isLoading && filteredItems.length > 0 && viewMode === 'table' && (
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36' />
                    <th className='pl-0 pr-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      File
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20'>
                      Ratio
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28'>
                      Logo
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Platforms
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Tags
                    </th>
                    {/* Ads column hidden until the creative-asset API exposes ad_count */}
                    {/* <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-16'>
                      Ads
                    </th> */}
                    <th className='w-16' />
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {filteredItems.map((asset) => {
                    const fits = asset.width_px && asset.height_px ? getPlatformFits(asset.width_px, asset.height_px) : [];
                    const adRatio = asset.width_px && asset.height_px ? getAdAspectRatio(asset.width_px, asset.height_px) : null;
                    const isPrimaryLogo = asset.tags.some((t) => PRIMARY_LOGO_TAG_VALUES.has(t.tag_value));
                    const canBeLogo = qualifiesAsPrimaryLogo(asset);
                    const filename = asset.original_filename ?? `Asset ${asset.id}`;
                    const allTags = asset.tags.filter((t) => !PRIMARY_LOGO_TAG_VALUES.has(t.tag_value));

                    return (
                      <tr key={asset.id} className='hover:bg-gray-50'>
                        {/* Thumbnail */}
                        <td className='px-6 py-4 whitespace-nowrap w-36'>
                          <div className='w-24 h-16 rounded-md overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center'>
                            <img
                              src={asset.signed_url}
                              alt={filename}
                              className='max-h-full max-w-full object-contain'
                              loading='lazy'
                            />
                          </div>
                        </td>

                        {/* Filename + dimensions */}
                        <td className='pl-0 pr-6 py-4'>
                          <div className='text-base font-medium text-gray-900 truncate max-w-[220px]' title={filename}>
                            {filename}
                          </div>
                          {asset.width_px && asset.height_px && (
                            <div className='text-sm text-gray-500'>{asset.width_px}×{asset.height_px}</div>
                          )}
                        </td>

                        {/* Ratio */}
                        <td className='px-6 py-4 w-20'>
                          {adRatio ? <RatioChip ratio={adRatio} /> : <span className='text-sm text-gray-500'>-</span>}
                        </td>

                        {/* Logo */}
                        <td className='px-6 py-4 w-28'>
                          {isPrimaryLogo ? (
                            <LogoChip
                              showUnset={mode === 'library'}
                              disabled={setLogo.isPending || unsetLogo.isPending}
                              onUnset={() => handleUnsetPrimaryLogo(asset)}
                            />
                          ) : canBeLogo && mode === 'library' ? (
                            <SetLogoButton
                              disabled={setLogo.isPending || unsetLogo.isPending}
                              onClick={() => handleSetPrimaryLogo(asset)}
                            />
                          ) : (
                            <span className='text-sm text-gray-500'>-</span>
                          )}
                        </td>

                        {/* Platform fit */}
                        <td className='px-6 py-4'>
                          <div className='flex flex-wrap gap-1'>
                            {fits.length > 0 ? (
                              fits.map((fit) => <PlatformFitChip key={fit} fit={fit} />)
                            ) : (
                              <span className='text-sm text-gray-900'>-</span>
                            )}
                          </div>
                        </td>

                        {/* Tags */}
                        <td className='px-6 py-4'>
                          <div className='flex flex-wrap gap-1 items-center'>
                            {allTags.map((tag) => (
                              <TagChip
                                key={tag.id}
                                tag={tag}
                                canRemove={mode === 'library'}
                                onRemove={() => handleRemoveUserTag(asset, tag.tag_value)}
                              />
                            ))}
                            {mode === 'library' && (
                              tableTagAssetId === asset.id ? (
                                <TagInput
                                  value={tableTagValue}
                                  disabled={isSubmittingTableTag}
                                  onChange={setTableTagValue}
                                  onSubmit={handleSubmitTableTag}
                                  onCancel={() => { setTableTagAssetId(null); setTableTagValue(''); }}
                                />
                              ) : (
                                <AddTagButton onClick={() => { setTableTagAssetId(asset.id); setTableTagValue(''); }} />
                              )
                            )}
                          </div>
                        </td>

                        {/* Ads column hidden until the creative-asset API exposes ad_count */}
                        {/* <td className='px-6 py-4 text-right'>
                          <div className='text-sm text-gray-900'>-</div>
                        </td> */}

                        {/* Actions */}
                        <td className='px-3 py-4'>
                          <div className='flex items-center gap-1 justify-end'>
                            {mode === 'library' && (
                              <button
                                disabled={isDeleting}
                                className='p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50'
                                onClick={() => requestRemove(asset.id)}
                                title='Remove image'
                              >
                                <Trash2 className='h-4 w-4' />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Infinite-scroll sentinel + "loading more" row (#5383). */}
          {!listQuery.isLoading && filteredItems.length > 0 && <div ref={loadMoreRef} className='h-px' />}
          {isFetchingNextPage && (
            <div className='flex items-center justify-center py-6 text-sm text-gray-500'>
              <Spinner size="sm" className="mr-2" /> Loading more
            </div>
          )}

        </>
      )}

      <ConformationModal
        isOpen={!!deleteAssetId}
        onClose={() => setDeleteAssetId(null)}
        onConfirm={confirmRemove}
        title="Remove Image"
        description="Are you sure you want to remove this image? This cannot be undone."
        confirmText="Remove"
        isLoading={isDeleting}
      />
    </div>
  );
};
